// ./src/utils/api/CrewDmService.ts
//
// Crew DIRECT MESSAGES (roadmap #6, PM epic — crew DM) host service. OpResult-native. A private chat between
// the account's crew (admin-app users) about an event: 1:1, a hand-picked group, or a whole role. Reuses the
// same DM engine as the guest surface (PublicDmService) but host-authed — identity + account come from the
// JWT server-side (CrewDmCore), so the client passes only the eventId. Messages reuse the guest-safe comment
// shape and render through the shared CommentBody. See [[eventsmagic-comments]].
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

/** A crew member the caller can message (autosuggest) — a team-role event guest, by event_guest id + role. */
export interface CrewDmDirectoryEntry {
  /** The event_guest id — the crew member's addressable DM principal (pass it as a recipient to start()). */
  id: string;
  /** Legacy: a browser session id when the crew member has opened the event; not needed to address them. */
  visitorId?: string | null;
  name?: string | null;
  /** The crew member's event role (host | co_host | organizer | staff | volunteer | …). */
  role?: string | null;
  /** Always true now — every crew member is addressable the moment they're on the roster (a message waits). */
  reachable?: boolean | null;
}

/** A participant of a crew DM conversation. */
export interface CrewDmParticipant {
  /** Opaque member cookie — a visitorId (crew) or host:<userId> (the admin). */
  id: string;
  name?: string | null;
  /** True for the current viewer. */
  isMe?: boolean | null;
}

/** A crew DM conversation the caller is part of. */
export interface CrewDmConversation {
  id: string;
  participants?: CrewDmParticipant[] | null;
  lastMessageAt?: string | null;
  lastMessageText?: string | null;
  /** True when the caller sent the last message. */
  lastMessageMine?: boolean | null;
  /** The event's Team conversation — everyone on the crew, kept in step with the roster (listed first). */
  isTeam?: boolean | null;
}

/** A crew DM message (the guest-safe comment shape). */
export interface CrewDmMessage {
  id: string;
  channelCommentId?: string | null;
  commenterName?: string | null;
  commenterKind?: string | null;
  commentText?: string | null;
  hasReplies?: boolean | null;
  createdAt?: string | null;
  reactions?: { reaction: string; count: number; mine: boolean }[] | null;
  mine?: boolean | null;
  editable?: boolean | null;
}

const MESSAGE_FIELDS = `
  id
  channelCommentId
  commenterName
  commenterKind
  commentText
  hasReplies
  createdAt
  reactions { reaction count mine }
  mine
  editable
`;

const CONVERSATION_FIELDS = `
  id
  participants { id name isMe }
  lastMessageAt
  lastMessageText
  lastMessageMine
  isTeam
`;

const DIRECTORY_QUERY = gql`
  query CrewDmDirectory($eventId: ID!, $query: String) {
    crewDmDirectory(eventId: $eventId, query: $query) {
      code
      errors { name errors warnings }
      data { id visitorId name role reachable }
    }
  }
`;

const LIST_QUERY = gql`
  query CrewDmList($eventId: ID!) {
    crewDmList(eventId: $eventId) {
      code
      errors { name errors warnings }
      data { ${CONVERSATION_FIELDS} }
    }
  }
`;

const THREAD_QUERY = gql`
  query CrewDmThread($eventId: ID!, $conversationId: ID!, $page: Int, $pageSize: Int) {
    crewDmThread(eventId: $eventId, conversationId: $conversationId, page: $page, pageSize: $pageSize) {
      code
      errors { name errors warnings }
      data { ${MESSAGE_FIELDS} }
    }
  }
`;

// #128/#133 — "Load older": a SEPARATE document, so the newest-page read above never depends on the cursor arguments —
// a client deployed before the gateway recomposes keeps every thread readable (only Load older waits for it).
const THREAD_OLDER_QUERY = gql`
  query CrewDmThreadOlder($eventId: ID!, $conversationId: ID!, $page: Int, $pageSize: Int, $beforeId: ID!, $before: String) {
    crewDmThread(eventId: $eventId, conversationId: $conversationId, page: $page, pageSize: $pageSize, beforeId: $beforeId, before: $before) {
      code
      errors { name errors warnings }
      data { ${MESSAGE_FIELDS} }
    }
  }
`;

const START_MUTATION = gql`
  mutation CrewDmStart($eventId: ID!, $recipientEventGuestIds: [ID], $roles: [String]) {
    crewDmStart(eventId: $eventId, recipientEventGuestIds: $recipientEventGuestIds, roles: $roles) {
      code
      errors { name errors warnings }
      data { ${CONVERSATION_FIELDS} }
    }
  }
`;

const POST_MUTATION = gql`
  mutation CrewDmPost($eventId: ID!, $conversationId: ID!, $commentText: String!) {
    crewDmPost(eventId: $eventId, conversationId: $conversationId, commentText: $commentText) {
      code
      errors { name errors warnings }
      data { ${MESSAGE_FIELDS} }
    }
  }
`;

const REACT_MUTATION = gql`
  mutation CrewDmReact($eventId: ID!, $conversationId: ID!, $channelCommentId: ID!, $reaction: String!) {
    crewDmReact(eventId: $eventId, conversationId: $conversationId, channelCommentId: $channelCommentId, reaction: $reaction) {
      code
      errors { name errors warnings }
      data { id reactions { reaction count mine } }
    }
  }
`;

export class CrewDmService {
  /** Autosuggest of the account's crew to message on an owned event (excludes the caller). */
  static async directory(eventId: string, query?: string | null): Promise<OpResult> {
    return ApiService.executeQuery(DIRECTORY_QUERY, { eventId, query: query ?? null }, { fetchPolicy: "network-only" });
  }

  /** The caller's crew DM conversations on an event (newest activity first). */
  static async list(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, { eventId }, { fetchPolicy: "network-only" });
  }

  /**
   * Read a conversation's messages (must be a participant). Newest first. `beforeId` + `before` (#128/#133) — the
   * oldest message on screen and its createdAt (`cursorOf`) — read the page older than it.
   */
  static async thread(args: {
    eventId: string;
    conversationId: string;
    page?: number | null;
    pageSize?: number | null;
    beforeId?: string | null;
    before?: string | null;
  }): Promise<OpResult> {
    return ApiService.executeQuery(
      args.beforeId ? THREAD_OLDER_QUERY : THREAD_QUERY,
      {
        eventId: args.eventId,
        conversationId: args.conversationId,
        page: args.page ?? null,
        pageSize: args.pageSize ?? null,
        ...(args.beforeId ? { beforeId: args.beforeId, before: args.before ?? null } : {}),
      },
      { fetchPolicy: "network-only" }
    );
  }

  /** Start (or reopen) a crew conversation with hand-picked crew (their event_guest ids) and/or whole roles
   *  (idempotent server-side). Crew are addressable by roster id even before they've opened the event. */
  static async start(eventId: string, args: { recipientEventGuestIds?: string[]; roles?: string[] }): Promise<OpResult> {
    return ApiService.executeMutation(START_MUTATION, {
      eventId,
      recipientEventGuestIds: args.recipientEventGuestIds ?? [],
      roles: args.roles ?? [],
    });
  }

  /** Send a message into a conversation the caller is part of. */
  static async post(args: { eventId: string; conversationId: string; commentText: string }): Promise<OpResult> {
    return ApiService.executeMutation(POST_MUTATION, {
      eventId: args.eventId,
      conversationId: args.conversationId,
      commentText: args.commentText,
    });
  }

  /** Toggle a reaction on a message in a conversation the caller is part of; returns the message's fresh summary. */
  static async react(args: { eventId: string; conversationId: string; channelCommentId: string; reaction: string }): Promise<OpResult> {
    return ApiService.executeMutation(REACT_MUTATION, {
      eventId: args.eventId,
      conversationId: args.conversationId,
      channelCommentId: args.channelCommentId,
      reaction: args.reaction,
    });
  }
}
